/**
 * CAOS-749 — the settings installed packages contribute to My Profile › Preferences.
 *
 * A package that has a per-person choice to offer had nowhere to put it. Setup is org
 * administration and a preference is not that, so the shell gained a `profile-preferences` seam: a
 * package injects into it and DECLARES one setting — its key, its label, how it is chosen, and what
 * a person who has never chosen sees. This module is the host end of that seam. It takes the
 * declarations off the shell frame plan, draws a control for each, and reads and writes the person's
 * value through the same preference store the theme uses.
 *
 * Two things are deliberately not here.
 *
 * The stored KEY is not the declared one. A package declares `key` within its own namespace and the
 * value is stored at `package.<sourceKey>.<key>` — the kernel derives that and refuses any key no
 * installed package declares (`kernel/src/preferences/person-preferences.ts`). So two packages
 * cannot collide, a package cannot write `theme`, and the closed vocabulary the preference store was
 * built around stays closed while still being extensible.
 *
 * An unrecognised `kind` draws NOTHING rather than guessing. The kinds are closed at deploy, so a
 * kind this file does not know means the workspace is older than the package that declared it. A
 * guessed control would write a value of the wrong type into a real preference, and the kernel would
 * refuse the write — leaving a control that visibly does nothing. Skipping is the honest answer, and
 * {@link unsupportedPreferences} is what lets the section say so rather than silently shrink.
 */
import type { ShellFramePlan, ShellInjectionRef } from '../kernel-contract.js';
import { PREFERENCES_CHANGE_EVENT, preference, setPreference } from '../person-preferences.js';

/** The seam a package injects into to contribute one setting. */
export const PROFILE_PREFERENCES_SEAM = 'profile-preferences';

/** One contributed setting, resolved to the key it is actually stored under. */
export interface ContributedPreference {
  /** The contributing package's key — what makes two packages' settings distinct. */
  readonly sourceKey: string;
  /** Where the value lives in the preference store. */
  readonly storageKey: string;
  readonly label: string;
  readonly blurb?: string;
  readonly kind?: string;
  readonly default?: unknown;
  readonly options?: readonly { readonly value: string; readonly label: string }[];
}

/** The kinds this workspace knows how to draw a control for. */
const DRAWN = new Set(['boolean', 'select']);

/**
 * The stored key for a contributed setting. Mirrors the kernel's `preferenceStorageKey`, which is
 * the authority — this is a client that has to name the same location, not a second decision. A
 * drift between the two shows up immediately: the kernel refuses to store a key nothing declares.
 */
export function preferenceStorageKey(sourceKey: string, key: string): string {
  return `package.${sourceKey}.${key}`;
}

/**
 * The contributed settings on a shell frame plan, in the order the plan carries them (the kernel has
 * already applied the org's seam ordering). An injection on the seam that carries no declaration
 * contributes nothing — the deploy gate refuses that shape, so a plan holding one is a plan from
 * before this seam existed.
 */
export function contributedPreferences(plan: Pick<ShellFramePlan, 'injections'>): ContributedPreference[] {
  const refs: readonly ShellInjectionRef[] = plan.injections[PROFILE_PREFERENCES_SEAM] ?? [];
  const out: ContributedPreference[] = [];
  for (const ref of refs) {
    const p = ref.preference;
    if (!p?.key) continue;
    out.push({
      sourceKey: ref.sourceKey,
      storageKey: preferenceStorageKey(ref.sourceKey, p.key),
      // A declaration with no label has nothing to show the person; its own key is a poor name but
      // it is the only one there is, and an unlabelled row beats a row that is silently missing.
      label: p.label ?? p.key,
      ...(p.blurb ? { blurb: p.blurb } : {}),
      ...(p.kind ? { kind: p.kind } : {}),
      ...(p.default !== undefined ? { default: p.default } : {}),
      ...(p.options ? { options: p.options } : {}),
    });
  }
  return out;
}

/** Those this workspace cannot draw a control for — see the module note on an unrecognised kind. */
export function unsupportedPreferences(list: readonly ContributedPreference[]): ContributedPreference[] {
  return list.filter((p) => !DRAWN.has(p.kind ?? ''));
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/**
 * What the control should show: the person's stored value when there is one, else what the package
 * declared. `preference()` already resolves the person's own value over the org default over the
 * platform's — and the kernel folds a contributed default into that resolution — so the fallback
 * here only matters before the preferences have loaded at all.
 */
function shown(p: ContributedPreference): unknown {
  const stored = preference(p.storageKey);
  return stored === undefined ? p.default : stored;
}

/**
 * One drawn control: the element, and the way to put it back in step with the store.
 *
 * A control does not listen for itself. The card listens once for all of them and calls the ones a
 * change names — a page of settings should cost the document one listener, not one per row.
 */
interface Drawn {
  readonly key: string;
  readonly group: HTMLElement;
  readonly sync: () => void;
}

/**
 * The one control both kinds draw: the segmented picker the theme already uses, so a contributed
 * setting looks like a setting of this workspace rather than like something bolted on.
 *
 * A boolean is a two-choice picker rather than a lone toggling button on purpose — a switch whose
 * label changes as you press it makes the person read the control to find out what it currently is,
 * and both states are worth seeing at once when one of them is the package's default.
 */
function segmented(p: ContributedPreference, choices: readonly { value: unknown; label: string }[]): Drawn {
  const group = el('div', 'caos-profile__segmented');
  group.setAttribute('role', 'group');
  group.setAttribute('aria-label', p.label);
  const buttons: { value: unknown; button: HTMLButtonElement }[] = [];
  const reflect = (value: unknown): void => {
    for (const b of buttons) b.button.setAttribute('aria-pressed', b.value === value ? 'true' : 'false');
  };
  for (const choice of choices) {
    const button = el('button', 'caos-profile__seg', choice.label);
    button.type = 'button';
    button.addEventListener('click', () => {
      // Reflect first: the store answers asynchronously, and a control that waits for the network to
      // move reads as broken. The listener below is what makes that safe rather than optimistic —
      // the store announces every key it settles on, including when it settles back.
      reflect(choice.value);
      void setPreference(p.storageKey, choice.value);
    });
    buttons.push({ value: choice.value, button });
    group.append(button);
  }
  reflect(shown(p));
  return { key: p.storageKey, group, sync: () => reflect(shown(p)) };
}

const BOOLEAN_CHOICES: readonly { value: unknown; label: string }[] = [
  { value: false, label: 'Off' },
  { value: true, label: 'On' },
];

function control(p: ContributedPreference): Drawn {
  return segmented(p, p.kind === 'boolean' ? BOOLEAN_CHOICES : (p.options ?? []).map((o) => ({ value: o.value, label: o.label })));
}

/**
 * The Preferences card holding every contributed setting, or `undefined` when nothing is
 * contributed — an empty card headed "From your packages" would be a promise of something that is
 * not there. A workspace that is offered settings it cannot draw still gets the card, carrying a
 * line that says so: see the note at the end.
 */
export function renderContributedPreferences(list: readonly ContributedPreference[]): HTMLElement | undefined {
  const drawable = list.filter((p) => DRAWN.has(p.kind ?? ''));
  const skipped = unsupportedPreferences(list);
  if (drawable.length === 0 && skipped.length === 0) return undefined;

  const root = el('section', 'caos-profile__section');
  const head = el('div', 'caos-profile__section-head');
  head.append(
    el('h2', 'caos-profile__section-title', 'From your packages'),
    el('p', 'caos-profile__section-note', 'Settings the packages installed here add to your profile.'),
  );
  root.append(head);

  const drawn: Drawn[] = [];
  for (const p of drawable) {
    const row = el('div', 'caos-profile__pref-row');
    row.dataset['preferenceKey'] = p.storageKey;
    const text = el('div', 'caos-profile__pref-text');
    text.append(el('div', 'caos-profile__pref-label', p.label));
    if (p.blurb) text.append(el('p', 'caos-profile__pref-blurb', p.blurb));
    const made = control(p);
    drawn.push(made);
    row.append(text, made.group);
    root.append(row);
  }

  // The store is the authority, and it can disagree with what was just pressed: a write the kernel
  // refuses (the package was uninstalled between this page loading and the click, say) settles the
  // cached value back, and a control that only ever reflected at construction would go on showing
  // the choice forever. Every other reader of this cache listens the same way — the theme switch and
  // the edge rail both do — so this is how a control stays honest, not an extra.
  if (drawn.length > 0) {
    const onChange = (ev: Event): void => {
      const keys = (ev as CustomEvent<{ keys?: readonly string[] }>).detail?.keys;
      for (const d of drawn) if (!keys || keys.includes(d.key)) d.sync();
    };
    document.addEventListener(PREFERENCES_CHANGE_EVENT, onChange);
    // And detach once the card leaves the screen, so moving between sections does not leak a
    // listener per visit — the same shape the Appearance card uses for the theme event.
    const observer = new MutationObserver(() => {
      if (root.isConnected) return;
      document.removeEventListener(PREFERENCES_CHANGE_EVENT, onChange);
      observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // A setting this workspace cannot draw is SAID, not silently dropped. A person told a package adds
  // a setting, who then finds nothing where it should be, has no way to learn that the workspace is
  // simply older than the package — and the absence looks like the package being broken.
  if (skipped.length > 0) {
    const note = el('p', 'caos-profile__pref-blurb');
    note.dataset['skippedPreferences'] = String(skipped.length);
    const names = skipped.map((p) => p.label).join(', ');
    note.textContent =
      skipped.length === 1
        ? `“${names}” needs a newer version of this workspace to be shown here.`
        : `${skipped.length} settings (${names}) need a newer version of this workspace to be shown here.`;
    root.append(note);
  }
  return root;
}
